// @ts-check
// Campo de resposta da Caixa (contrato 10 de atendimento, seções 10, 12.1 e 24.2): abas "Responder" e "Nota interna",
// "/" para as respostas prontas com a prévia das variáveis, "@" para mencionar alguém na nota, anexos, aviso da janela de
// 24 horas com o modelo de Atendimento da 2.0.0, faixa de presença ("Ana está digitando") e "Chegou mensagem nova",
// que bloqueia o envio até a pessoa ver (o servidor recusa com NEW_MESSAGES_SINCE_VIEW).
import { html } from 'htm/preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { listReplyTemplates, renderReplyTemplate } from '../../data/service-desk.js';
import { loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { filterReplyTemplates, mentionQueryAt, presenceText, replyTemplateQueryOf, serializeNoteMentions, MAXIMUM_NOTE_MENTIONS } from '../../lib/service-inbox-rules.js';
import { renderServiceTemplate } from '../../../../shared/service-desk/reply-template-variables.js';
import { PendingAttachmentList, usePendingAttachments } from '../conversations/message-composer.js';
import { IconButton, LightIconButton, Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { t } from '../../i18n/index.js';
import { serviceErrorMessage } from './service-display.js';

const MAX_TEXTAREA_HEIGHT = 200;

/**
 * @typedef {import('../../data/service-desk.js').ReplyTemplate} ReplyTemplate
 * @typedef {import('../../lib/service-inbox-rules.js').CollisionState} CollisionState
 * @typedef {{ text: string, attachments: import('../conversations/message-composer.js').UploadedAttachment[], replyTemplateId: string | null }} ReplyInput
 */

/**
 * Faixa de colisão sobre o campo: quem mais está na conversa e a mensagem nova não vista.
 * @param {{ collision: CollisionState, onSeeNewMessages: () => void }} props
 */
export function CollisionBanner({ collision, onSeeNewMessages }) {
  const presence = presenceText(collision);
  if (!presence && !collision.hasNewMessage) return null;
  return html`<div class="service-collision" data-testid="service-collision">
    ${presence ? html`<span class="service-collision-presence" data-typing=${collision.typers.length > 0 ? 'true' : undefined}>
      <${Icon} name=${collision.typers.length > 0 ? 'IconPencil' : 'IconEye'} size=${14} />${t(presence.key, presence.variables)}
    </span>` : null}
    ${collision.hasNewMessage
      ? html`<span class="service-collision-new" role="alert">
          <${Icon} name="IconMessage" size=${14} />${t('serviceDesk.collision.newMessage')}
          <${Button} title=${t('serviceDesk.collision.seeNewMessage')} size="small" variant="secondary" onClick=${onSeeNewMessages} />
        </span>`
      : null}
  </div>`;
}

/**
 * @param {{ mode: 'reply' | 'note', onModeChange: (mode: 'reply' | 'note') => void, canReply: boolean, isWindowClosed: boolean, onSendTemplate?: () => void,
 *   ticketId: string | null, templateValues: Record<string, string | null>, collision: CollisionState, onSeeNewMessages: () => void,
 *   onReply: (input: ReplyInput) => Promise<boolean>, onNote: (input: { body: string, mentions: string[] }) => Promise<boolean>, onTyping: () => void,
 *   focusRequest: number, replyDisabledReason?: string | null }} props
 */
export function ServiceComposer({
  mode, onModeChange, canReply, isWindowClosed, onSendTemplate, ticketId, templateValues, collision, onSeeNewMessages, onReply, onNote, onTyping, focusRequest, replyDisabledReason,
}) {
  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [templates, setTemplates] = useState(/** @type {ReplyTemplate[] | null} */ (null));
  const [highlighted, setHighlighted] = useState(0);
  const [usedTemplate, setUsedTemplate] = useState(/** @type {{ id: string, missingVariables: string[] } | null} */ (null));
  const [mentions, setMentions] = useState(/** @type {Array<{ workspaceMemberId: string, label: string }>} */ ([]));
  const [caret, setCaret] = useState(0);
  const textareaRef = useRef(/** @type {HTMLTextAreaElement | null} */ (null));
  const fileInputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const pending = usePendingAttachments();
  const isNote = mode === 'note' || !canReply;

  useEffect(() => {
    if (focusRequest > 0) textareaRef.current?.focus();
  }, [focusRequest]);
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(MAX_TEXTAREA_HEIGHT, textarea.scrollHeight)}px`;
  }, [text]);

  const templateQuery = !isNote ? replyTemplateQueryOf(text) : null;
  useEffect(() => {
    if (templateQuery === null || templates) return;
    listReplyTemplates().then(setTemplates).catch(() => setTemplates([]));
  }, [templateQuery !== null]);
  const templateMatches = templateQuery !== null ? filterReplyTemplates(templates ?? [], templateQuery) : [];

  const mentionQuery = isNote ? mentionQueryAt(text, caret) : null;
  useEffect(() => {
    if (mentionQuery) loadWorkspaceMembers();
  }, [Boolean(mentionQuery)]);
  const mentionMatches = mentionQuery
    ? (workspaceMembersState.value ?? []).filter((member) => formatFullName(member.name).toLowerCase().includes(mentionQuery.query.toLowerCase())).slice(0, 6)
    : [];

  /** @param {ReplyTemplate} template */
  const chooseTemplate = async (template) => {
    let rendered = renderServiceTemplate(template.body, templateValues);
    if (ticketId) {
      try {
        rendered = await renderReplyTemplate(template.id, ticketId);
      } catch {
        // sem a prévia do servidor, a da tela com os valores que ela já tem
      }
    }
    setText(rendered.text);
    setUsedTemplate({ id: template.id, missingVariables: rendered.missingVariables ?? [] });
    setHighlighted(0);
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  /** @param {any} member */
  const chooseMention = (member) => {
    if (!mentionQuery || mentions.length >= MAXIMUM_NOTE_MENTIONS) return;
    const label = formatFullName(member.name);
    const next = `${text.slice(0, mentionQuery.start)}@${label} ${text.slice(caret)}`;
    setText(next);
    setMentions((current) => (current.some((mention) => mention.workspaceMemberId === member.id) ? current : [...current, { workspaceMemberId: member.id, label }]));
    setHighlighted(0);
    requestAnimationFrame(() => {
      const position = mentionQuery.start + label.length + 2;
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(position, position);
      setCaret(position);
    });
  };

  const uploaded = pending.getUploaded();
  const isBlocked = !isNote && (collision.isSendBlocked || isWindowClosed);
  const canSend = !isSending && !pending.isUploading && !isBlocked && (text.trim() !== '' || (!isNote && uploaded.length > 0)) && templateQuery === null;

  const submit = async () => {
    if (!canSend) return;
    setIsSending(true);
    setError(null);
    try {
      const isDone = isNote
        ? await onNote(serializeNoteMentions(text.trim(), mentions))
        : await onReply({ text: text.trim(), attachments: uploaded, replyTemplateId: usedTemplate?.id ?? null });
      if (isDone) {
        setText('');
        setMentions([]);
        setUsedTemplate(null);
        pending.clear();
      }
    } catch (requestError) {
      setError(serviceErrorMessage(requestError));
    } finally {
      setIsSending(false);
    }
  };

  const popoverItems = templateQuery !== null ? templateMatches : mentionMatches;
  /** @param {KeyboardEvent} event */
  const handleKeyDown = (event) => {
    if (popoverItems.length > 0 && (templateQuery !== null || mentionQuery)) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlighted((current) => (current + (event.key === 'ArrowDown' ? 1 : -1) + popoverItems.length) % popoverItems.length);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        const item = popoverItems[Math.min(highlighted, popoverItems.length - 1)];
        if (templateQuery !== null) chooseTemplate(/** @type {ReplyTemplate} */ (item));
        else chooseMention(item);
        return;
      }
    }
    if (event.key === 'Escape' && templateQuery !== null) {
      event.preventDefault();
      setText('');
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  const placeholder = isNote ? t('serviceDesk.composer.notePlaceholder')
    : isWindowClosed ? t('conversations.composer.placeholderClosed') : t('serviceDesk.composer.replyPlaceholder');
  return html`<div class="service-composer" data-mode=${isNote ? 'note' : 'reply'} data-testid="service-composer">
    <${CollisionBanner} collision=${collision} onSeeNewMessages=${onSeeNewMessages} />
    ${!isNote && isWindowClosed
      ? html`<div class="service-window-closed" role="status">
          <${Icon} name="IconAlertTriangle" size=${14} /><span>${t('serviceDesk.composer.windowClosed')}</span>
          ${onSendTemplate ? html`<${Button} title=${t('serviceDesk.composer.sendTemplate')} icon="IconSend" size="small" accent="blue" onClick=${onSendTemplate} />` : null}
        </div>`
      : null}
    <div class="service-composer-tabs" role="tablist">
      <button type="button" role="tab" class="service-composer-tab" aria-selected=${!isNote ? 'true' : 'false'} data-active=${!isNote ? 'true' : undefined}
        disabled=${!canReply} title=${!canReply ? replyDisabledReason ?? undefined : undefined} onClick=${() => onModeChange('reply')}>
        <${Icon} name="IconSend" size=${14} />${t('serviceDesk.composer.reply')}
      </button>
      <button type="button" role="tab" class="service-composer-tab" aria-selected=${isNote ? 'true' : 'false'} data-active=${isNote ? 'true' : undefined}
        data-note="true" onClick=${() => onModeChange('note')}>
        <${Icon} name="IconNotes" size=${14} />${t('serviceDesk.composer.note')}
      </button>
      <span class="service-composer-hint">${isNote ? t('serviceDesk.composer.noteHint') : t('serviceDesk.composer.replyHint')}</span>
    </div>
    ${popoverItems.length > 0 || (templateQuery !== null && templates)
      ? html`<div class="service-composer-popover" role="listbox" aria-label=${templateQuery !== null ? t('serviceDesk.composer.replyTemplates') : t('serviceDesk.composer.mentionMember')}>
          ${templateQuery !== null && templateMatches.length === 0
            ? html`<div class="service-composer-popover-empty">${t('serviceDesk.composer.noTemplates')}</div>`
            : null}
          ${templateQuery !== null
            ? templateMatches.map((template, index) => html`<button key=${template.id} type="button" role="option" class="service-composer-option"
                aria-selected=${index === highlighted ? 'true' : 'false'} data-highlighted=${index === highlighted ? 'true' : undefined}
                onMouseDown=${(/** @type {Event} */ event) => {
                  event.preventDefault();
                  chooseTemplate(template);
                }}>
                <span class="service-composer-option-title">${template.title}${template.shortcut ? html`<code>/${template.shortcut}</code>` : null}
                  <span class="service-scope-chip">${t(`serviceDesk.replyTemplates.scopes.${template.scope}`)}</span></span>
                <span class="service-composer-option-body">${template.body}</span>
              </button>`)
            : mentionMatches.map((member, index) => html`<button key=${member.id} type="button" role="option" class="service-composer-option"
                aria-selected=${index === highlighted ? 'true' : 'false'} data-highlighted=${index === highlighted ? 'true' : undefined}
                onMouseDown=${(/** @type {Event} */ event) => {
                  event.preventDefault();
                  chooseMention(member);
                }}>
                <span class="service-composer-option-title"><${Icon} name="IconAt" size=${14} />${formatFullName(member.name)}</span>
              </button>`)}
        </div>`
      : null}
    ${usedTemplate && usedTemplate.missingVariables.length > 0 && !isNote
      ? html`<div class="service-composer-warning" role="status"><${Icon} name="IconInfoCircle" size=${14} />
          ${t('serviceDesk.composer.missingVariables', { variables: usedTemplate.missingVariables.map((name) => t(`serviceDesk.templateVariables.${name.replace('.', '_')}`)).join(', ') })}</div>`
      : null}
    ${!isNote ? html`<${PendingAttachmentList} attachments=${pending.attachments} onRemove=${pending.removeAttachment} />` : null}
    <div class="service-composer-row">
      ${!isNote
        ? html`<${LightIconButton} icon="IconPaperclip" size="medium" accent="tertiary" ariaLabel=${t('conversations.composer.attach')} disabled=${isBlocked}
            onClick=${() => fileInputRef.current?.click()} />
          <input ref=${fileInputRef} type="file" multiple hidden onChange=${(/** @type {Event} */ event) => {
            const input = /** @type {HTMLInputElement} */ (event.currentTarget);
            pending.addFiles(input.files);
            input.value = '';
          }} />`
        : html`<span class="service-composer-note-icon"><${Icon} name="IconLock" size=${14} /></span>`}
      <textarea
        ref=${textareaRef}
        class="service-composer-input"
        rows="2"
        value=${text}
        disabled=${!isNote && isWindowClosed}
        placeholder=${placeholder}
        aria-label=${placeholder}
        data-testid="service-composer-input"
        onInput=${(/** @type {Event} */ event) => {
          const target = /** @type {HTMLTextAreaElement} */ (event.currentTarget);
          setText(target.value);
          setCaret(target.selectionStart ?? target.value.length);
          setHighlighted(0);
          if (usedTemplate && !target.value) setUsedTemplate(null);
          if (!isNote) onTyping();
        }}
        onKeyUp=${(/** @type {Event} */ event) => setCaret(/** @type {HTMLTextAreaElement} */ (event.currentTarget).selectionStart ?? 0)}
        onClick=${(/** @type {Event} */ event) => setCaret(/** @type {HTMLTextAreaElement} */ (event.currentTarget).selectionStart ?? 0)}
        onKeyDown=${handleKeyDown}
      ></textarea>
      <${IconButton} icon=${isNote ? 'IconNotes' : 'IconSend'} accent="blue" size="medium" disabled=${!canSend} onClick=${submit}
        ariaLabel=${isNote ? t('serviceDesk.composer.addNote') : t('serviceDesk.composer.send')} data-testid="service-composer-send" />
    </div>
    ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
  </div>`;
}
